<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { Badge, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell } from 'flowbite-svelte';
	import RowAction from './RowAction.svelte';
	import { listScalingNodes, scaleDownGroup, dismissScalingNode, retryScalingTeardown } from '../../AppNetwork';
	import type { ScalingNode, ScalingNodeState } from '../../AppTypes';

	export let groupId: number;

	let nodes: ScalingNode[] = [];
	let loading = true;
	let error = '';
	let refreshTimer: ReturnType<typeof setInterval> | null = null;

	const STATE_BADGES: Record<ScalingNodeState, { color: 'green' | 'red' | 'yellow' | 'indigo' | 'dark'; label: string }> = {
		provisioning: { color: 'indigo', label: 'Provisioning' },
		booting: { color: 'indigo', label: 'Booting' },
		joining: { color: 'indigo', label: 'Joining swarm' },
		labeling: { color: 'indigo', label: 'Labeling' },
		registering_lb: { color: 'indigo', label: 'Registering LB' },
		active: { color: 'green', label: 'Active' },
		draining: { color: 'yellow', label: 'Draining' },
		detaching_lb: { color: 'yellow', label: 'Detaching LB' },
		removing: { color: 'yellow', label: 'Removing' },
		gone: { color: 'dark', label: 'Gone' },
		failed: { color: 'red', label: 'Failed' }
	};

	async function load() {
		try {
			nodes = (await listScalingNodes(groupId)).items;
			error = '';
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load nodes';
		}
		loading = false;
	}

	onMount(() => {
		load();
		// Provisioning takes minutes — keep the view live while expanded.
		refreshTimer = setInterval(load, 10000);
	});
	onDestroy(() => {
		if (refreshTimer) clearInterval(refreshTimer);
	});

	async function removeNode(node: ScalingNode) {
		if (!confirm(`Scale down node "${node.hetznerServerName}"?`)) return;
		try {
			await scaleDownGroup(groupId, node.id);
			await load();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to scale down';
		}
	}

	async function retryTeardown(node: ScalingNode) {
		if (!confirm(`Retry teardown of "${node.hetznerServerName}"? It will be drained, detached and deleted.`)) return;
		try {
			await retryScalingTeardown(groupId, node.id);
			await load();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to retry teardown';
		}
	}

	async function dismiss(node: ScalingNode) {
		if (!confirm(`Dismiss node "${node.hetznerServerName}"? A leftover server (if any) will be deleted.`)) return;
		try {
			await dismissScalingNode(groupId, node.id);
			await load();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to dismiss node';
		}
	}
</script>

{#if loading}
	<p class="text-sm text-gray-400 py-4 text-center">Loading nodes...</p>
{:else if error}
	<p class="text-sm text-red-500 py-2">{error}</p>
{:else if nodes.length === 0}
	<p class="text-sm text-gray-400 py-4 text-center">No nodes — scale up to provision the first server.</p>
{:else}
	<Table>
		<TableHead class="bg-gray-100 dark:bg-gray-700">
			<TableHeadCell>Server</TableHeadCell>
			<TableHeadCell>State</TableHeadCell>
			<TableHeadCell>IPv4</TableHeadCell>
			<TableHeadCell>Swarm node</TableHeadCell>
			<TableHeadCell>Detail</TableHeadCell>
			<TableHeadCell></TableHeadCell>
		</TableHead>
		<TableBody>
			{#each nodes as node (node.id)}
				{@const badge = STATE_BADGES[node.state]}
				<TableBodyRow class="group">
					<TableBodyCell class="font-medium text-gray-900 dark:text-white">{node.hetznerServerName}</TableBodyCell>
					<TableBodyCell><Badge color={badge.color}>{badge.label}</Badge></TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400 font-mono text-xs">{node.ipv4 ?? '—'}</TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400 font-mono text-xs truncate max-w-[10rem]">{node.swarmNodeId ?? '—'}</TableBodyCell>
					<TableBodyCell class="text-xs {node.lastError ? 'text-red-500' : 'text-gray-400'} truncate max-w-xs" title={node.lastError ?? ''}>
						{node.lastError ?? (node.state === 'active' ? 'Healthy' : `since ${new Date(node.stateEnteredAt).toLocaleTimeString()}`)}
					</TableBodyCell>
					<TableBodyCell>
						<div class="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
							{#if node.state === 'active'}
								<RowAction icon="fa-arrow-down" label="Scale down this node" danger on:click={() => removeNode(node)} />
							{:else if node.state === 'failed'}
								<RowAction icon="fa-rotate-right" label="Retry teardown (drain → detach → delete)" on:click={() => retryTeardown(node)} />
								<RowAction icon="fa-xmark" label="Dismiss" danger on:click={() => dismiss(node)} />
							{/if}
						</div>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>
{/if}
